/**
 * error-boundary — a React error boundary around the routed `<App>`.
 *
 * The studio's EAGER genome is composed through the NO-VALIDATE build (#518,
 * gate 8) so the v3 cross-ref validator stays off the entry chunk. Boot safety
 * is therefore no longer "the compose throws → the `main.tsx` try/catch renders
 * BootFailure"; a genome whose bad ref survives the un-validated compose would
 * instead surface as a THROW during route resolution / render. This boundary
 * catches exactly that — any render-time throw under `<App>` renders the same
 * {@link BootFailure} diagnostic surface (a --os-* themed DOM surface, never a
 * blank screen and never a native dialog, gate 9).
 *
 * Only a class component can be a React error boundary; this is the studio's one.
 *
 * @module @ubiclouds/v5-studio/app/error-boundary
 */

import { Component, lazy, Suspense, type ErrorInfo, type ReactNode } from 'react';

// #572 gate-8 reclaim: BootFailure is EXCEPTIONAL-path UI (a crashed render /
// failed boot), so it loads lazily — off the eager entry chunk (the #528
// PanelTransportError precedent). The null Suspense fallback is a momentary
// blank while the tiny chunk loads, immediately replaced by the diagnostic.
const BootFailure = lazy(() =>
  import('./boot-failure.js').then((m) => ({ default: m.BootFailure })),
);

interface AppErrorBoundaryProps {
  readonly children: ReactNode;
}

interface AppErrorBoundaryState {
  readonly error: unknown;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  override state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Surface the crash to the console for diagnosis; the UI shows BootFailure.
    console.error('[v5-studio] render error caught by AppErrorBoundary', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error != null) {
      return (
        <Suspense fallback={null}>
          <BootFailure error={this.state.error} />
        </Suspense>
      );
    }
    return this.props.children;
  }
}
